import clsx from "clsx";
import type { ReactNode } from "react";

/**
 * La franja de marcador y la cifra de marcador de la sección 8.4.
 *
 * QUÉ ES
 *   Tres celdas —presupuesto, valor de plantilla y puntos— separadas por 1 px, con un filete de
 *   acento arriba y **pegadas al campo**, sin espacio entre medias. Es la lectura de un solo
 *   vistazo: el estado de la temporada en tres números grandes.
 *
 * POR QUÉ LAS CIFRAS VAN EN DISPLAY
 *   Barlow Condensed es la voz de marcador de la sección 8.4. Con `tabular-nums` el ancho de cada
 *   dígito es fijo, así que la franja no se mueve cuando el presupuesto cambia de 9,9 M a 10,1 M.
 */

export type StatTone = "neutral" | "money" | "up" | "down";

const TONES: Record<StatTone, string> = {
  neutral: "text-ink",
  money: "text-money",
  up: "text-up",
  down: "text-down",
};

export interface StatProps {
  label: string;
  value: ReactNode;
  /** Segunda etiqueta bajo la cifra, como en el rótulo sobre el campo. */
  caption?: ReactNode;
  tone?: StatTone;
  /** Tamaño de la cifra en píxeles: 26 en la franja, 34 en tarjeta, 40 en el rótulo. */
  size?: number;
  className?: string;
}

export function Stat({
  label,
  value,
  caption,
  tone = "neutral",
  size = 34,
  className,
}: StatProps) {
  return (
    <div className={clsx("grid gap-0.5", className)}>
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted">
        {label}
      </span>
      <span
        className={clsx(
          "font-display font-semibold leading-none tabular-nums",
          TONES[tone],
        )}
        style={{ fontSize: `${size}px` }}
      >
        {value}
      </span>
      {caption ? (
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted">
          {caption}
        </span>
      ) : null}
    </div>
  );
}

export interface ScorelineCell {
  label: string;
  value: ReactNode;
  tone?: StatTone;
}

export interface ScorelineProps {
  cells: ScorelineCell[];
  className?: string;
}

export function Scoreline({ cells, className }: ScorelineProps) {
  return (
    <div
      className={clsx(
        "grid grid-cols-3 overflow-hidden border-t-[3px] border-pitch bg-surface",
        className,
      )}
    >
      {cells.map((cell) => (
        <div
          key={cell.label}
          className="border-r border-border px-3 py-2.5 last:border-r-0"
        >
          <Stat
            label={cell.label}
            value={cell.value}
            tone={cell.tone}
            size={26}
          />
        </div>
      ))}
    </div>
  );
}
